import { SaveButton, TabContentWrapper } from '@/components/_common';
import {
  InvoiceItemCreateRequest,
  InvoiceItemFormValues,
  TypeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useCreateInvoiceNoteItems } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import { PageProps } from '@/types/PageProps';
import { Form, message } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import InvoiceNoteItemsSelectPartTabs from './InvoiceNoteItemsSelectPartTabs';
import { usePartMutation } from '@/core/domain/operational/Parts/Parts.hooks';
import { useEffect, useState } from 'react';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useGetInvoiceId } from '@/core/domain/invoice/invoice/invoice.hooks';

const initialTabErrors = {
  general: false,
  icms: false,
};

export default function InvoiceNoteItemsSelectPartForm({ route }: PageProps) {
  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;
  const { invoicePartId } = useRouteMatch<{ invoicePartId: string }>().params;
  const intl = useIntl().formatMessage;
  const [tabErrors, setTabErrors] = useState(initialTabErrors);
  const [loadingValidate, setLoadingValidate] = useState(false);

  const [form] = Form.useForm<InvoiceItemFormValues>();

  const partMutation = usePartMutation();

  const { data: invoice } = useGetInvoiceId(Number(invoiceId));

  const { data } = usePartStockPrices(Number(invoicePartId));

  const companyPrice = data?.result?.find(
    (item) => item.company.id === invoice?.companyId,
  );

  useEffect(() => {
    if (invoicePartId) {
      partMutation.mutateAsync(Number(invoicePartId));
    }
  }, [invoicePartId]);

  useEffect(() => {
    if (partMutation.data) {
      form.setFieldsValue({
        description: partMutation.data.name,
        ncmId: partMutation.data.ncmId,
        cfopId: partMutation.data.cfopId,
        unitMeasurement: partMutation.data.unit,
        valueUnitary: companyPrice?.sellPrice,
      });
    }
  }, [partMutation.data]);

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useCreateInvoiceNoteItems();

  const history = useHistory();

  const invoiceType = useLocation().pathname.split('/datas')[0].slice(20, 23);

  function handleTabError(key: keyof typeof initialTabErrors) {
    setTabErrors((state) => ({ ...state, [key]: true }));
  }

  const handleSubmit = async () => {
    const { ...formValues } = await form.validateFields();

    const obj: InvoiceItemCreateRequest = {
      ...formValues,
      invoiceId: Number(invoiceId),
      partId: Number(invoicePartId),
      taskId: undefined,
      type: TypeEnum.part,
      consumableId: undefined,
      itemTaxes: {
        ...formValues.itemTaxes,
        withheld: formValues.itemTaxes.withheld
          ? Boolean(JSON.parse(formValues.itemTaxes.withheld))
          : undefined,
        federalTaxesWithheld: formValues.itemTaxes.federalTaxesWithheld
          ? Boolean(JSON.parse(formValues.itemTaxes.federalTaxesWithheld))
          : undefined,
      },
    };

    await createMutation(obj);

    history.push(`/app/fiscal/invoice/${invoiceType}/${invoiceId}/noteItens`);
  };

  async function onSubmit() {
    setLoadingValidate(true);
    try {
      setTabErrors(initialTabErrors);

      const general = await form
        .validateFields([
          'description',
          'unitMeasurement',
          'ncmId',
          'cfopId',
          'quantity',
          'valueUnitary',
        ])
        .catch(() => handleTabError('general'));

      const icms = await form
        .validateFields([
          ['itemTaxes', 'icmsCst'],
          ['itemTaxes', 'icmsOrigin'],
        ])
        .catch(() => handleTabError('icms'));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasDataValues = (data: any) =>
        Object.values(data).every((value) => !!value);

      if (
        !general ||
        !hasDataValues(general) ||
        !icms ||
        !hasDataValues(icms)
      ) {
        throw new Error('Preencha todos os campos');
      }

      handleSubmit();
    } catch {
      message.error('Há campos a serem validados!');
    } finally {
      setLoadingValidate(false);
    }
  }

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SaveButton
          type="primary"
          onClick={onSubmit}
          disabled={loadingValidate}
          loading={isLoadingCreate || loadingValidate}
        >
          {intl({ id: 'commons.entity.save' })}
        </SaveButton>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      <InvoiceNoteItemsSelectPartTabs
        tabErrors={tabErrors}
        invoiceId={Number(invoiceId)}
        invoiceType={invoiceType}
        form={form}
        invoice={invoice}
      />
    </TabContentWrapper>
  );
}
